import $ from "jquery";
import { Component, lazy, Suspense } from "react";
import { HashRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import Modal from "react-modal";
import { medplum } from '../medplumClient';
import { getProxyToken } from '../lib/proxyAuthToken';

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.min.js";
import "../stylesheets/app.css";

// App shell — always needed, load eagerly
import Navbar from "./navbar";
import Sidebar from "./sidebar";
import RequireRole from "./RequireRole";

// Route-level components — loaded only when the route is visited
const Forgot = lazy(() => import("./forgot"));
const Login = lazy(() => import("./login"));
const Logout = lazy(() => import("./logout"));
const ProxyLogin = lazy(() => import("./proxy-login"));
const ResetPassword = lazy(() => import("./reset-password"));
const Signup = lazy(() => import("./signup"));

const CompareReports = lazy(() => import("../report-composer/compare-reports"));
const ConnectionCenter = lazy(() => import("./connectionCenter/connectionCenter"));
const Connections = lazy(() => import("./connections"));
const AdminManager = lazy(() => import("./deviceAdmin/AdminManager"));
const Devices = lazy(() => import("./devices"));
const Organization = lazy(() => import("./organization"));
const Patient = lazy(() => import("./patient"));
const Patients = lazy(() => import("./patients"));
const Profile = lazy(() => import("./profile"));
const Report = lazy(() => import("./report"));
const Reports = lazy(() => import("./reports"));
const ReportDiagnostics = lazy(() => import("./report-diagnostics"));
const Settings = lazy(() => import("./settings"));
const User = lazy(() => import("./user"));
const VideoTours = lazy(() => import("./video-tours"));

const Members = lazy(() => import("./members"));
const Payment = lazy(() => import("./payment"));
const Physicians = lazy(() => import("./physicians"));
const Subscriptions = lazy(() => import("./subscriptions"));

const Analysis = lazy(() => import("../SleepScreen/Screens/AnalysisScreen"));
const Charts = lazy(() => import("./Chart/Charts"));
const Chart2 = lazy(() => import("./Chart2/page.jsx"));

const Test = lazy(() => import("./test"));

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('App error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return <div style={{ padding: '2rem' }}>Something went wrong. Please refresh the page.</div>;
    }
    return this.props.children;
  }
}

// Bridges class-based render methods to v6 Route element prop.
// In v4, Route render prop received { location, match, history } — reconstruct that shape.
function RouteRenderer({ render }) {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const routeProps = {
    location,
    match: { url: location.pathname, path: location.pathname, params },
    history: {
      push: navigate,
      replace: (to) => navigate(to, { replace: true }),
    },
  };
  return render(routeProps) ?? null;
}


// AdminRoute was a frontend role gate that redirected non-admins to
// /patients. It kept regressing: while useCurrentRole was still loading
// the role, the gate rendered the "denied" branch (a Navigate), so any
// path that mounted these routes before the role cache warmed up
// bounced the user to /patients -- even for admins. The backend role
// gates in middlewares/require-role.js are the source of truth, so this
// frontend wrapper is now a pass-through. If frontend gating returns,
// do NOT navigate during the loading state.
function AdminRoute({ render }) {
  return <RouteRenderer render={render} />;
}

Modal.setAppElement("#root");

class App extends Component {
  constructor(props) {
    super(props);

    var userId = parseInt(localStorage.getItem("userId")) || 0;
    var organizationId = parseInt(localStorage.getItem("organizationId")) || 0;

    var theme = this.getInitialTheme();
    this.state = {
      user: {
        id: userId,
      },

      organization: { id: organizationId },
      organizations: [],
      isAdmin: false,
      theme: theme,
      isSidebarCollapsed: true,
    };
    // Heartbeat to verify the server still considers us logged in.
    // Was 60 minutes — far too slow when sessions are 30 min: a user could
    // sit on a broken page for up to an hour after their session expired
    // before the poll noticed and redirected them.
    this.loginCheckFrequency = 1000 * 60;   // 60 seconds
    this.getUser = this.getUser.bind(this);
    this.getOrganizations = this.getOrganizations.bind(this);
    this.toggleTheme = this.toggleTheme.bind(this);
    this.toggleSidebarCollapse = this.toggleSidebarCollapse.bind(this);
    if (this.state.user.id) {
      this.getUserInterval = setInterval(this.getUser, this.loginCheckFrequency);
    }
    if (this.state.organization.id) {
      this.getOrganizations();
    }
  }

  componentDidMount() {
    this.applyTheme(this.state.theme);

    // Attach Medplum Bearer token to every jQuery request as a fallback auth mechanism.
    // This covers the case where the session cookie isn't present on the first few
    // post-login requests (e.g. due to timing between session write and the client's
    // next request). The medplum-bearer strategy on the backend accepts this token.
    $.ajaxSetup({
      beforeSend: function (xhr) {
        try {
          const token = medplum.getAccessToken() || getProxyToken();
          if (token) xhr.setRequestHeader('Authorization', 'Bearer ' + token);
        } catch (_) {}
      },
    });

    // Global 401 handler: any jQuery request that returns 401 means the server
    // dropped our session. Re-run getUser immediately so the user gets bounced
    // to /#/login on the next event loop tick instead of waiting up to a full
    // poll interval. Throttled so a burst of failing requests doesn't fire
    // getUser many times in a row.
    $(document).ajaxError((event, jqXHR, settings) => {
      if (jqXHR.status !== 401) return;
      // Don't react to the heartbeat itself — getUser already handles its own 401
      if ((settings.url || '').replace(/[?#].*$/, '').endsWith('/login')) return;
      const now = Date.now();
      if (this._lastUnauthCheck && now - this._lastUnauthCheck < 2000) return;
      this._lastUnauthCheck = now;
      this.getUser();
    });

    this.getUser();
    this.prefetchHeavyChunks();
  }

  prefetchHeavyChunks() {
    const prefetch = () => {
      import("./Chart/Charts");
      import("./Chart2/page.jsx");
      import("../SleepScreen/Screens/AnalysisScreen");
    };
    if ("requestIdleCallback" in window) {
      requestIdleCallback(prefetch, { timeout: 5000 });
    } else {
      setTimeout(prefetch, 2000);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.theme !== this.state.theme) {
      this.applyTheme(this.state.theme);
    }
  }

  componentWillUnmount() {
    document.body.removeAttribute("data-theme");
  }

  getInitialTheme() {
    var storedTheme = localStorage.getItem("theme");
    if (storedTheme === "dark" || storedTheme === "light") {
      return storedTheme;
    }

    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }

    return "light";
  }

  applyTheme(theme) {
    document.body.setAttribute("data-theme", theme);
  }

  toggleTheme() {
    this.setState(function (prevState) {
      var nextTheme = prevState.theme === "dark" ? "light" : "dark";
      localStorage.setItem("theme", nextTheme);
      return { theme: nextTheme };
    });
  }

  toggleSidebarCollapse() {
    this.setState(function (prevState) {
      return { isSidebarCollapsed: !prevState.isSidebarCollapsed };
    });
  }

  getUser() {
    var success = this.handleUser.bind(this);
    $.ajax({
      method: "GET",
      url: "/login",
    }).done(success)
      .fail((jqXHR) => {
        if (jqXHR.status === 401) {
          console.log('[401] href=', window.location.href);
          console.log('[401] hash=', window.location.hash);
          console.log('[401] hashPath=', this.getHashPath());
          console.log('[401] isPublicRoute=', this.isPublicRoute());
          // Stop polling
          if (this.getUserInterval) {
            clearInterval(this.getUserInterval);
            this.getUserInterval = null;
          }

          // Only clear Medplum tokens if the user was actively logged in before this 401.
          // Calling medplum.clear() on a fresh unauthenticated load races with the
          // login flow and wipes the token mid-login.
          const wasLoggedIn = !!this.state.user.id;
          if (wasLoggedIn) {
            try {
              medplum.clear();
            } catch (e) {
              console.warn('Failed to clear Medplum client:', e);
            }
          }

          // Clear local storage/session (keep organizationId so the last org is restored on next login)
          try {
            sessionStorage.setItem('skipAutoLoginOnce', '1');
            localStorage.removeItem('rememberMe');
            localStorage.removeItem('email');
            localStorage.removeItem('user');
            localStorage.removeItem('userId');
            localStorage.removeItem('systemAdmin');
            localStorage.removeItem('super');
          } catch (e) {
            console.warn('Failed to clear local storage on 401:', e);
          }

          // Reset state first
          this.setState({
            user: {},
            organization: { id: 0 },
            organizations: [],
            isAdmin: false,
          });

          // IMPORTANT: Don't force redirect if we're on a public route
          // (forgot/reset/invite links should be usable while logged out)
          if (this.isPublicRoute()) {
            return;
          }

          // Otherwise redirect to login for protected routes
          window.location.replace('/#/login');
        }
      });
  }

  getHashPath() {
    // HashRouter examples:
    //   "#/reset?token=abc" => "/reset"
    //   "#/forgot" => "/forgot"
    const hash = window.location.hash || '';
    const noHash = hash.startsWith('#') ? hash.slice(1) : hash; // "/reset?token=abc"
    const pathOnly = noHash.split('?')[0] || '/';
    return pathOnly;
  }

  isPublicRoute() {
    const publicPaths = ['/login', '/forgot', '/reset', '/signup', '/proxy'];
    const currentPath = this.getHashPath();
    return publicPaths.some((p) => currentPath === p || currentPath.startsWith(p + '/'));
  }

  handleUser(data) {
    var userStateChanged = this.state.user.id !== data.id;
    if (userStateChanged || !this.state.user.firstName) {
      if (data.id) {
        this.getUserInterval = setInterval(this.getUser, this.loginCheckFrequency);
        const lastOrgId = parseInt(localStorage.getItem('lastOrgId')) || 0;
        if (lastOrgId) localStorage.removeItem('lastOrgId');
        const restoredOrgId = lastOrgId || parseInt(localStorage.getItem('organizationId')) || 0;
        this.setState({ user: data, organization: { id: restoredOrgId } }, this.getOrganizations);
        localStorage.setItem("username", data.username);
        localStorage.setItem("email", data.email);
        localStorage.setItem("userId", data.id);
        localStorage.setItem("systemAdmin", data.systemAdmin);
        localStorage.setItem("super", data.super == 1 || data.super === true ? '1' : '0');
        //ns.fconf.ENABLE_REP_EDITOR = data.systemAdmin+'' === '1';
      } else {
        clearInterval(this.getUserInterval);
        this.setState({
          user: {
            id: null,
          },
        });
        localStorage.removeItem("userId");
        localStorage.removeItem("systemAdmin");
        localStorage.removeItem("super");
        //ns.fconf.ENABLE_REP_EDITOR = false;
      }
    }
    //console.log( 'data.systemAdmin='+ data.systemAdmin );
    //ns.fconf.ENABLE_REP_EDITOR = data.systemAdmin+'' === '1';
  }

  // Uses the user's cookie to get all associated organizations
  getOrganizations() {
    var success = this.handleOrganizations.bind(this);
    $.get("/organizations").done(success);
  }

  // Adds all associated organizations to state as an array
  handleOrganizations(data) {
    var success = this.getOrganization.bind(this);
    this.setState(
      {
        organizations: data.rows,
      },
      success
    );
  }

  getOrganization() {
    var id = this.state.organization.id;
    let organizationMatch = false;

    if (!this.state.organizations || this.state.organizations.length === 0) {
      this.setState({
        organization: { id: 0 },
      });
      return;
    }

    // Compares locally stored organization id to each organization associated with the user to find a match.
    this.state.organizations.forEach((org) => {
      if (org.id === id) {
        organizationMatch = true;
      }
    });

    var success = this.handleOrganization.bind(this);
    var firstOrg = this.state.organizations[0];

    if (!id) {
      // No stored org — fall back to first membership org.
      if (firstOrg) {
        $.get("/organizations/" + firstOrg.id).done(success);
      } else {
        this.setState({ organization: { id: 0 } });
      }
      return;
    }

    if (!organizationMatch) {
      // Stored org not in the membership list (e.g. accessed via admin privileges).
      // Try fetching it directly before silently switching to a different org.
      $.get("/organizations/" + id)
        .done(success)
        .fail(() => {
          if (firstOrg) {
            $.get("/organizations/" + firstOrg.id).done(success);
          } else {
            this.setState({ organization: { id: 0 } });
          }
        });
      return;
    }

    $.get("/organizations/" + id).done(success);
  }

  handleOrganization(data) {
    if (this.state.organizationId !== data.id) {
      localStorage.removeItem("organizationId");
      localStorage.setItem("organizationId", data.id);
    }
    var success = this.getMembers.bind(this);
    this.setState({ organization: data }, success);
  }

  getMembers() {
    var success = this.handleMembers.bind(this);
    $.ajax({
      url: "/members",
      data: {
        userId: this.state.user.id,
        organizationId: this.state.organization.id,
      },
    }).done(success);
  }

  handleMembers(data) {
    var members = data.rows;
    var userId = this.state.user.id;
    var membership = members.find(function (member) {
      return member.userId === userId;
    });
    if (!membership) return;
    var isAdmin = membership.roleId === 1;
    this.setState({
      isAdmin: isAdmin,
    });
  }

  ensureActiveOrganization() {
    var activeOrg = this.state.organization;
    var matchingOrg =
      this.state.organizations.find(function (org) {
        return activeOrg.id === org.id;
      }) ||
      this.state.organizations[0] ||
      {};
    if (matchingOrg.id && matchingOrg.id !== activeOrg.id) {
      this.handleOrganization(matchingOrg);
    }
  }

  async logout() {
    // stop polling so nothing rehydrates state mid-logout
    if (this.getUserInterval) {
      clearInterval(this.getUserInterval);
      this.getUserInterval = null;
    }

    try {
      // Ensure we have a valid access token, then revoke it
      await medplum.refreshIfExpired().catch(() => {});
      if (medplum.getAccessToken()) {
        await medplum.signOut();  // POST /oauth2/logout + clears local tokens
      } else {
        // No token on this instance (likely a multiple-instance issue) — hard clear anyway
        medplum.clear();
      }
    } catch {
      // If revoke 401s for any reason, still clear local auth so we don't silently re-auth
      medplum.clear();
    }

    // Kill your own server session
    try {
      await $.ajax({ method: 'GET', url: '/logout', cache: false });
    } catch {}

    // Save UI prefs from state — the Medplum sign-out above clears the whole
    // localStorage, so anything we want to survive logout must be re-persisted
    // here. Org is restored on next login via lastOrgId; theme is read by
    // getInitialTheme() on mount and otherwise falls back to the OS setting.
    try {
      if (this.state.organization.id) {
        localStorage.setItem('lastOrgId', this.state.organization.id);
      }
      if (this.state.theme) {
        localStorage.setItem('theme', this.state.theme);
      }
    } catch {}

    try {
      sessionStorage.setItem('skipAutoLoginOnce', '1');
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('email');
      localStorage.rememberMe = 'off';
      localStorage.removeItem('user');
      localStorage.removeItem('userId');
      localStorage.removeItem('systemAdmin');
      localStorage.removeItem('super');
    } catch {}

    // Clean slate
    this.setState({ user: {}, organization: { id: 0 }, organizations: [], isAdmin: false }, () => {
      window.location.replace('/#/login'); // HashRouter
    });
  }


  // Removes locally stored user and set the state's user to empty
  // handleLogout(data) {
  //   sessionStorage.setItem('skipAutoLoginOnce', '1');
  //   localStorage.removeItem('rememberMe');
  //   localStorage.removeItem('email');
  //   localStorage.removeItem("user");
  //   localStorage.removeItem("organization");
  //   this.setState({
  //     user: {},
  //   });
  // }

  handleError(error) {
    console.log("Error: " + error);
  }

  createOrganization() {
    $.ajax({
      method: "POST",
      url: "/organizations",
      data: this.state,
    }).done(this.updateOrganization.bind(this));
  }

  requireUser() {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
  }

  renderDefault(location) {
    if (this.state.user.id) {
      return <Navigate to="/patients" replace />;
    } else {
      return <Navigate to="/login" replace />;
    }
  }

  renderLogin(location) {
    if (this.state.user.id) {
      return <Navigate to="/patients" replace />;
    }
    return (
      <Login
        location={location}
        user={this.state.user}
        handleUser={this.handleUser.bind(this)}
        theme={this.state.theme}
        toggleTheme={this.toggleTheme}
      />
    );
  }

  renderProxyLogin(location) {
    if (this.state.user.id) {
      return <Navigate to="/patients" replace />;
    }
    return <ProxyLogin location={location} user={this.state.user} handleUser={this.handleUser.bind(this)} />;
  }

  renderLogout(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return <Logout location={location} user={this.state.user} logout={this.logout.bind(this)} />;
  }

  renderOrganization(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return (
      <Organization
        location={location}
        user={this.state.user}
        organization={this.state.organization}
        getOrganizations={this.getOrganizations.bind(this)}
      />
    );
  }

  renderPatients(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return (
      <Patients
        location={location}
        user={this.state.user}
        organization={this.state.organization}
        includeInMultiNight_dataRetriever={this.includeInMultiNight_dataRetriever}
      />
    );
  }

  renderPatient(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    //kvk ... where did it get pat data?

    return (
      <Patient
        location={location}
        user={this.state.user}
        organization={this.state.organization}
        includeInMultiNight_dataRetriever={this.includeInMultiNight_dataRetriever}
      />
    );
  }

  renderReports(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return (
      <Reports
        location={location}
        organization={this.state.organization}
        user={this.state.user}
        includeInMultiNight_dataRetriever={this.includeInMultiNight_dataRetriever}
      />
    );
  }

  renderReport(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return <Report location={location} organization={this.state.organization} user={this.state.user} />;
  }

  renderUser(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return <User location={location} user={this.state.user} />;
  }

  renderProfile(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return (
      <Profile
        location={location}
        user={this.state.user}
        organization={this.state.organization}
        isAdmin={this.state.isAdmin}
      ></Profile>
    );
  }

  renderPayment(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return <Payment location={location} user={this.state.user} organization={this.state.organization} />;
  }

  renderSubscriptions(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return <Subscriptions location={location} user={this.state.user} organization={this.state.organization} />;
  }

  renderMembers(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return (
      <Members
        location={location}
        user={this.state.user}
        organization={this.state.organization}
        isAdmin={this.state.isAdmin}
      />
    );
  }

  renderPhysicians(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return <Physicians location={location} user={this.state.user} organization={this.state.organization} />;
  }

  renderDevices(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return <Devices location={location} user={this.state.user} organization={this.state.organization} />;
  }

  renderAdminManager(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return <AdminManager location={location} user={this.state.user} organization={this.state.organization} />;
  }

  renderConnectionCenter(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return <ConnectionCenter location={location} user={this.state.user} organization={this.state.organization} />;
  }

  renderResetPassword(location) {
    return <ResetPassword location={location} theme={this.state.theme} toggleTheme={this.toggleTheme} />;
  }

  renderForgot(location) {
    return <Forgot location={location} theme={this.state.theme} toggleTheme={this.toggleTheme} />;
  }

  renderSignup(location) {
    return <Signup location={location} theme={this.state.theme} toggleTheme={this.toggleTheme} />;
  }

  renderConnections(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return <Connections location={location} />;
  }

  renderSettings(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return <Settings location={location} organization={this.state.organization}></Settings>;
  }

  renderVideoTours(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return <VideoTours location={location} />;
  }

  renderCompareReports(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return (
      <CompareReports
        location={location}
        organization={this.state.organization}
        user={this.state.user}
        includeInMultiNight_dataRetriever={this.includeInMultiNight_dataRetriever}
      />
    );
  }

  renderAnalysis(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    return <Analysis location={location}></Analysis>;
  }

  renderChart(location) {
    return <Charts location={location} />;
  }

  renderTest(location) {
    return <Test location={location} />;
  }

  renderReportDiagnostics(location) {
    if (!this.state.user.id) {
      return <Navigate to="/login" replace />;
    }
    if (this.state.user.super !== true) {
      return <Navigate to="/reports" replace />;
    }
    return (
      <ReportDiagnostics
        location={location}
        organization={this.state.organization}
        handleOrganization={this.handleOrganization.bind(this)}
      />
    );
  }

  renderChart2(routeProps) {
    const chart2Key = `chart2-${routeProps.match?.url || routeProps.location?.pathname || ""}-${this.state.theme}`;

    return <Chart2 key={chart2Key} {...routeProps} theme={this.state.theme} />;
  }

  renderNavbar() {
    let isLoggedIn = this.state.user.id ? true : false;
    var data = {
      user: this.state.user,
      organization: this.state.organization,
      organizations: this.state.organizations,
    };
    var handle = {
      organization: this.handleOrganization.bind(this),
    };
    if (isLoggedIn) {
      return (
        <Navbar
          user={data.user}
          organization={data.organization}
          organizations={data.organizations}
          handleOrganization={handle.organization}
          logout={this.logout.bind(this)}
          theme={this.state.theme}
          toggleTheme={this.toggleTheme}
          isSidebarCollapsed={this.state.isSidebarCollapsed}
          toggleSidebarCollapse={this.toggleSidebarCollapse}
        />
      );
    }
    return null;
  }

  render(a, b, c) {
    var actions = {
      logout: this.logout.bind(this),
    };
    var render = {
      default: this.renderDefault.bind(this),
      analysis: this.renderAnalysis.bind(this),
      chart: this.renderChart.bind(this),
      login: this.renderLogin.bind(this),
      proxyLogin: this.renderProxyLogin.bind(this),
      logout: this.renderLogout.bind(this),
      organization: this.renderOrganization.bind(this),
      payment: this.renderPayment.bind(this),
      subscriptions: this.renderSubscriptions.bind(this),
      members: this.renderMembers.bind(this),
      physicians: this.renderPhysicians.bind(this),
      patients: this.renderPatients.bind(this),
      patient: this.renderPatient.bind(this),
      reports: this.renderReports.bind(this),
      report: this.renderReport.bind(this),
      user: this.renderUser.bind(this),
      connections: this.renderConnections.bind(this),
      videoTours: this.renderVideoTours.bind(this),
      reset: this.renderResetPassword.bind(this),
      forgot: this.renderForgot.bind(this),
      signup: this.renderSignup.bind(this),
      compareReports: this.renderCompareReports.bind(this),
      settings: this.renderSettings.bind(this),
      profile: this.renderProfile.bind(this),
      devices: this.renderDevices.bind(this),
      AdminManager: this.renderAdminManager.bind(this),
      ConnectionCenter: this.renderConnectionCenter.bind(this),
      test: this.renderTest.bind(this),
      reportDiagnostics: this.renderReportDiagnostics.bind(this),
      chart2: this.renderChart2.bind(this),
    };

    var data = {
      user: this.state.user,
      organization: this.state.organization,
      organizations: this.state.organizations,
    };
    return (
      <ErrorBoundary>
      <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div id="app" className={`app-shell theme-${this.state.theme}`}>
          <div className="app-sidebar-column">
            <Sidebar
              user={data.user}
              organization={data.organization}
              isAdmin={this.state.isAdmin}
              logout={actions.logout}
              isCollapsed={this.state.isSidebarCollapsed}
            />
          </div>

          <div id="viewport">
            {this.renderNavbar()}
            <Suspense fallback={<div style={{ padding: "2rem" }}>Loading…</div>}>
              <Routes>
                <Route path="/" element={<RouteRenderer render={render.default} />} />
                <Route path="/connections" element={<RouteRenderer render={render.connections} />} />
                <Route path="/video-tours" element={<RouteRenderer render={render.videoTours} />} />
                <Route path="/forgot" element={<RouteRenderer render={render.forgot} />} />
                <Route path="/login" element={<RouteRenderer render={render.login} />} />
                <Route path="/logout" element={<RouteRenderer render={render.logout} />} />
                <Route path="/members" element={<AdminRoute render={render.members} />} />
                <Route path="/multi-night-reports/:id" element={<RouteRenderer render={render.compareReports} />} />
                <Route path="/organizations/:id" element={<AdminRoute render={render.organization} />} />
                <Route path="/patients" element={<RouteRenderer render={render.patients} />} />
                <Route path="/patients/:id" element={<RouteRenderer render={render.patient} />} />
                <Route path="/payment" element={<AdminRoute render={render.payment} />} />
                <Route path="/physicians" element={<AdminRoute render={render.physicians} />} />
                <Route path="/proxy" element={<RouteRenderer render={render.proxyLogin} />} />
                <Route path="/reports" element={<RouteRenderer render={render.reports} />} />
                <Route path="/reports/:id" element={<RouteRenderer render={render.report} />} />
                <Route path="/reset" element={<RouteRenderer render={render.reset} />} />
                <Route path="/signup" element={<RouteRenderer render={render.signup} />} />
                <Route path="/subscriptions" element={<AdminRoute render={render.subscriptions} />} />
                <Route path="/test" element={<RouteRenderer render={render.test} />} />
                <Route path="/analysis" element={<RouteRenderer render={render.analysis} />} />
                <Route path="/chart2/:id/:patientId" element={<RouteRenderer render={render.chart2} />} />
                <Route path="/chart2/:id" element={<RouteRenderer render={render.chart2} />} />
                <Route path="/chart/:id" element={<RouteRenderer render={render.chart} />} />
                <Route path="/chart/:id/:patientId" element={<RouteRenderer render={render.chart} />} />
                <Route path="/profile" element={<RouteRenderer render={render.profile} />} />
                <Route path="/report-diagnostics" element={<RouteRenderer render={render.reportDiagnostics} />} />
                <Route path="/settings" element={<AdminRoute render={render.settings} />} />
                <Route path="/users/:id" element={<RouteRenderer render={render.user} />} />
                <Route path="/devices" element={<AdminRoute render={render.devices} />} />
                <Route path="/deviceAdmin" element={<AdminRoute render={render.AdminManager} />} />
                <Route path="/connectionCenter" element={<AdminRoute render={render.ConnectionCenter} />} />
              </Routes>
            </Suspense>
          </div>
        </div>
      </HashRouter>
      </ErrorBoundary>
    );
  }
}

export default App;
